package com.schoolconnect.app.feature.studentenrollment

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.unit.Dp
import com.schoolconnect.app.feature.imports.ImportCardActionButton
import com.schoolconnect.app.feature.imports.ImportErrorRow
import com.schoolconnect.app.feature.imports.ImportErrorTableCard
import com.schoolconnect.app.feature.imports.ImportReviewSummary
import com.schoolconnect.app.feature.imports.ImportReviewSummaryStrip
import com.schoolconnect.app.feature.imports.ImportStepUiModel
import com.schoolconnect.app.feature.imports.ImportSummaryCard
import com.schoolconnect.app.feature.imports.ImportSummaryRow
import com.schoolconnect.app.feature.imports.ImportTabUiModel
import com.schoolconnect.app.feature.imports.ImportWorkflowStage
import com.schoolconnect.app.feature.imports.ImportsStepper
import com.schoolconnect.app.designsystem.ApiLoadingOverlay
import com.schoolconnect.app.designsystem.AppConfirmationDialog
import com.schoolconnect.app.designsystem.AppConfirmationDialogTone
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerType
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.arrow_right
import ischoolconnect.sharedui.generated.resources.clipboard_check
import ischoolconnect.sharedui.generated.resources.circle_check
import ischoolconnect.sharedui.generated.resources.cloud_upload
import ischoolconnect.sharedui.generated.resources.database_download
import ischoolconnect.sharedui.generated.resources.document
import ischoolconnect.sharedui.generated.resources.download
import ischoolconnect.sharedui.generated.resources.excel_file
import ischoolconnect.sharedui.generated.resources.filter
import org.jetbrains.compose.resources.painterResource
import kotlinx.coroutines.launch
import com.schoolconnect.app.util.MAX_WORKBOOK_BYTES
import kotlinx.coroutines.yield

@Composable
fun StudentEnrollmentScreen(
    state: StudentEnrollmentUiState,
    onEvent: (StudentEnrollmentUiEvent) -> Unit,
    contextLine: String? = null,
    headerActions: @Composable RowScope.() -> Unit = {},
    contentHorizontalPadding: Dp = 24.dp,
    contentTopPadding: Dp = 22.dp,
    contentBottomPadding: Dp = 12.dp
) {
    val scrollState = rememberScrollState()

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(StudentEnrollmentBackground)
    ) {
        BoxWithConstraints(
            modifier = Modifier
                .fillMaxSize()
                .padding(
                    start = contentHorizontalPadding,
                    top = contentTopPadding,
                    end = contentHorizontalPadding,
                    bottom = contentBottomPadding
                )
        ) {
            val contentWidth = minOf(maxWidth, 1238.dp)
            when (state.stage) {
                StudentEnrollmentWorkflowStage.ValidationSummary -> {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .widthIn(max = contentWidth)
                            .fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        StudentEnrollmentPageHeader(
                            title = state.pageTitle,
                            subtitle = state.validationSubtitle,
                            detailText = contextLine?.takeIf { it.isNotBlank() } ?: state.contextLine,
                            headerActions = headerActions
                        )
                        Column(
                            modifier = Modifier
                                .weight(1f)
                                .fillMaxWidth()
                                .verticalScroll(scrollState),
                            verticalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            StudentEnrollmentValidationContent(
                                state = state,
                                onEvent = onEvent
                            )
                            Spacer(modifier = Modifier.height(8.dp))
                            StudentEnrollmentValidationFooter(
                                onCancel = { onEvent(StudentEnrollmentUiEvent.CancelClicked) },
                                onContinueReview = { onEvent(StudentEnrollmentUiEvent.ContinueReviewClicked) },
                                canContinue = state.serverValidationCompleted && !state.isLoading
                            )
                        }
                    }
                }

                StudentEnrollmentWorkflowStage.ReviewAndSave -> {
                    Column(
                        modifier = Modifier
                            .fillMaxSize()
                            .widthIn(max = contentWidth)
                            .fillMaxWidth(),
                        verticalArrangement = Arrangement.spacedBy(14.dp)
                    ) {
                        StudentEnrollmentPageHeader(
                            title = state.pageTitle,
                            subtitle = state.reviewSubtitle,
                            detailText = contextLine?.takeIf { it.isNotBlank() } ?: state.contextLine,
                            headerActions = headerActions
                        )

                        StudentEnrollmentReviewContent(
                            state = state,
                            onEvent = onEvent,
                            modifier = Modifier.weight(1f)
                        )

                        Spacer(modifier = Modifier.height(32.dp))

                        StudentEnrollmentReviewFooter(
                            backLabel = state.backToValidationSummaryLabel,
                            onBackToValidationSummary = {
                                onEvent(StudentEnrollmentUiEvent.BackToValidationSummaryClicked)
                            },
                            onCancel = { onEvent(StudentEnrollmentUiEvent.CancelClicked) },
                            onSaveRecords = { onEvent(StudentEnrollmentUiEvent.SaveRecordsClicked) },
                            readyToSaveRows = state.reviewSummary.readyToSaveRows,
                            canSave = state.serverValidationCompleted && !state.isLoading
                        )
                    }
                }
            }
        }

        ApiLoadingOverlay(
            isVisible = state.isLoading,
            title = "Updating data",
            subtitle = "Please wait while the workbook request finishes."
        )

        if (state.structureValidationErrors.isNotEmpty()) {
            AppConfirmationDialog(
                onDismiss = { onEvent(StudentEnrollmentUiEvent.DismissStructureErrors) },
                title = "Template structure issue",
                subtitle = "Fix these workbook issues before data validation can begin.",
                confirmLabel = "Got it",
                onConfirm = { onEvent(StudentEnrollmentUiEvent.DismissStructureErrors) },
                dismissLabel = "Close",
                tone = AppConfirmationDialogTone.Primary,
                bodyText = "Download the Student Enrollment template and fill in its data. Keep the tab names, tab order, and column headers unchanged.",
                content = {
                    Column(
                        modifier = Modifier.fillMaxWidth().heightIn(max = 210.dp).verticalScroll(rememberScrollState()),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        state.structureValidationErrors.forEachIndexed { index, issue ->
                            Text(
                                text = "${index + 1}. $issue",
                                color = StudentEnrollmentDanger,
                                fontSize = 13.sp,
                                lineHeight = 18.sp
                            )
                        }
                    }
                }
            )
        }

        if (state.showReviewWarning) {
            AppConfirmationDialog(
                onDismiss = { onEvent(StudentEnrollmentUiEvent.DismissReviewWarning) },
                title = "Review with validation issues?",
                subtitle = "There are issues in the Excel file.",
                confirmLabel = "OK",
                onConfirm = { onEvent(StudentEnrollmentUiEvent.ConfirmContinueReviewClicked) },
                dismissLabel = "Cancel",
                tone = AppConfirmationDialogTone.Primary,
                bodyText = "The workbook still contains validation issues. Do you still want to review the imported student and parent records?",
                content = {
                    StudentEnrollmentOutlineButton(
                        label = "Download Error Details",
                        icon = painterResource(Res.drawable.download),
                        onClick = { onEvent(StudentEnrollmentUiEvent.DownloadErrorDetailsClicked) },
                        modifier = Modifier.fillMaxWidth(),
                        borderColor = StudentEnrollmentPrimary,
                        contentColor = StudentEnrollmentPrimary,
                        height = 44.dp
                    )
                }
            )
        }

        if (state.showSaveWarning) {
            AppConfirmationDialog(
                onDismiss = { onEvent(StudentEnrollmentUiEvent.DismissSaveWarning) },
                title = "Save with validation issues?",
                subtitle = "There are issues in the Excel file.",
                confirmLabel = "Save Records",
                onConfirm = { onEvent(StudentEnrollmentUiEvent.ConfirmSaveRecordsClicked) },
                dismissLabel = "Cancel",
                tone = AppConfirmationDialogTone.Primary,
                bodyText = "The workbook still contains validation issues. Do you still want to save the student and parent records?",
                content = {
                    StudentEnrollmentOutlineButton(
                        label = "Download Error Details",
                        icon = painterResource(Res.drawable.download),
                        onClick = { onEvent(StudentEnrollmentUiEvent.DownloadErrorDetailsClicked) },
                        modifier = Modifier.fillMaxWidth(),
                        borderColor = StudentEnrollmentPrimary,
                        contentColor = StudentEnrollmentPrimary,
                        height = 44.dp
                    )
                }
            )
        }
    }

}

@Composable
private fun StudentEnrollmentValidationContent(
    state: StudentEnrollmentUiState,
    onEvent: (StudentEnrollmentUiEvent) -> Unit
) {
    val pickerScope = rememberCoroutineScope()
    val workbookPickerLauncher = rememberFilePickerLauncher(
        type = PickerType.File(listOf("xlsx")),
        title = "Select Student Enrollment Workbook"
    ) { selectedFile ->
        if (selectedFile == null) return@rememberFilePickerLauncher
        onEvent(StudentEnrollmentUiEvent.WorkbookLoadStarted(selectedFile.name))
        pickerScope.launch {
            // Give Compose one frame to render the loading overlay before file I/O begins.
            yield()
            val bytes = runCatching { selectedFile.readBytes() }
                .getOrElse {
                    onEvent(StudentEnrollmentUiEvent.WorkbookLoadFailed(it.message ?: "Unable to read the selected workbook"))
                    return@launch
                }
            if (bytes.size > MAX_WORKBOOK_BYTES) {
                onEvent(StudentEnrollmentUiEvent.WorkbookLoadFailed("Workbook is too large. Maximum size is 25 MB."))
                return@launch
            }
            onEvent(StudentEnrollmentUiEvent.WorkbookPrepared(selectedFile.name, bytes))
            val preview = parseStudentEnrollmentWorkbook(bytes, selectedFile.name)
                .getOrElse {
                    onEvent(StudentEnrollmentUiEvent.WorkbookLoadFailed(it.message ?: "Unable to parse the workbook"))
                    return@launch
                }
            onEvent(StudentEnrollmentUiEvent.WorkbookLoaded(preview))
        }
    }

    Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
        ImportsStepper(
            steps = state.steps.map { it.toImportStep() }
        )

        if (state.errorMessage.isNotBlank()) {
            Card(
                modifier = Modifier.fillMaxWidth(),
                colors = CardDefaults.cardColors(containerColor = StudentEnrollmentDangerBg),
                border = androidx.compose.foundation.BorderStroke(1.dp, StudentEnrollmentDanger.copy(alpha = 0.22f))
            ) {
                androidx.compose.material3.Text(
                    text = state.errorMessage,
                    color = StudentEnrollmentDanger,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(horizontal = 18.dp, vertical = 14.dp)
                )
            }
        }

        BoxWithConstraints(modifier = Modifier.fillMaxWidth()) {
            // Account for browser density, shell rail, and page padding so
            // wide desktop content shows all three summary cards in one row.
            val twoColumn = maxWidth < 1000.dp
            if (twoColumn) {
                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(14.dp)
                    ) {
                        StudentEnrollmentExistingDetailsCard(
                            rows = state.existingDetails,
                            modifier = Modifier.weight(1f),
                            onDownloadCurrentData = { onEvent(StudentEnrollmentUiEvent.DownloadCurrentDataClicked) },
                            onDownloadTemplate = { onEvent(StudentEnrollmentUiEvent.DownloadTemplateClicked) }
                        )
                        StudentEnrollmentNewImportDetailsCard(
                            rows = state.newImportDetails,
                            modifier = Modifier.weight(1f),
                            onUpload = { workbookPickerLauncher.launch() }
                        )
                    }
                    StudentEnrollmentValidationSummaryCard(
                        rows = state.validationDetails,
                        modifier = Modifier.fillMaxWidth(),
                        lastUpdated = state.validationLastUpdated,
                        onValidate = { onEvent(StudentEnrollmentUiEvent.ValidateFileClicked) }
                    )
                }
            } else {
                val cardWidth = ((maxWidth - 28.dp) / 3f).coerceAtLeast(0.dp)
                Row(horizontalArrangement = Arrangement.spacedBy(14.dp)) {
                    StudentEnrollmentExistingDetailsCard(
                        rows = state.existingDetails,
                        modifier = Modifier.width(cardWidth),
                        onDownloadCurrentData = { onEvent(StudentEnrollmentUiEvent.DownloadCurrentDataClicked) },
                        onDownloadTemplate = { onEvent(StudentEnrollmentUiEvent.DownloadTemplateClicked) }
                    )
                    StudentEnrollmentNewImportDetailsCard(
                        rows = state.newImportDetails,
                        modifier = Modifier.width(cardWidth),
                        onUpload = { workbookPickerLauncher.launch() }
                    )
                    StudentEnrollmentValidationSummaryCard(
                        rows = state.validationDetails,
                        modifier = Modifier.width(cardWidth),
                        lastUpdated = state.validationLastUpdated,
                        onValidate = { onEvent(StudentEnrollmentUiEvent.ValidateFileClicked) }
                    )
                }
            }
        }

        ImportErrorTableCard(
            totalErrorsLabel = state.totalErrorsLabel,
            helpText = state.errorHelpText,
            sheetOptions = state.sheetOptions,
            selectedSheet = state.selectedSheet,
            onSheetSelected = { onEvent(StudentEnrollmentUiEvent.SheetSelected(it)) },
            onFilterClick = { onEvent(StudentEnrollmentUiEvent.FilterClicked) },
            rows = state.errorRows.map { it.toImportErrorRow() },
            pageText = state.errorPageText,
            pageIndex = state.pageIndex,
            pageCount = state.pageCount,
            onPageChanged = { onEvent(StudentEnrollmentUiEvent.PageChanged(it)) }
        )
    }
}

@Composable
private fun StudentEnrollmentReviewContent(
    state: StudentEnrollmentUiState,
    onEvent: (StudentEnrollmentUiEvent) -> Unit,
    modifier: Modifier = Modifier
) {
    val selectedTabCount = state.reviewTabs.firstOrNull { it.id == state.selectedTabId }?.count ?: 0
    val pageIndex = state.reviewPageIndexByTab[state.selectedTabId] ?: 0
    val pageCount = studentEnrollmentPageCount(state.rowsPerPage, selectedTabCount)
    val pageText = studentEnrollmentPageText(state.rowsPerPage, pageIndex, selectedTabCount)

    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        ImportsStepper(
            steps = state.steps.map { it.toImportStep() }
        )

        ImportReviewSummaryStrip(
            summary = state.reviewSummary.toImportReviewSummary()
        )

        StudentEnrollmentTabRow(
            tabs = state.reviewTabs,
            onTabSelected = { onEvent(StudentEnrollmentUiEvent.TabSelected(it)) }
        )

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
        ) {
            when (state.selectedTabId) {
                "parents" -> StudentEnrollmentParentTable(
                    records = state.parentRecords,
                    pageIndex = pageIndex,
                    rowsPerPage = state.rowsPerPage,
                    onRowsPerPageChanged = { onEvent(StudentEnrollmentUiEvent.RowsPerPageChanged(it)) },
                    onPageChanged = { onEvent(StudentEnrollmentUiEvent.PageChanged(it)) },
                    modifier = Modifier.fillMaxSize(),
                    pageText = pageText,
                    pageCount = pageCount
                )
                else -> StudentEnrollmentStudentTable(
                    records = state.studentRecords,
                    pageIndex = pageIndex,
                    rowsPerPage = state.rowsPerPage,
                    onRowsPerPageChanged = { onEvent(StudentEnrollmentUiEvent.RowsPerPageChanged(it)) },
                    onPageChanged = { onEvent(StudentEnrollmentUiEvent.PageChanged(it)) },
                    modifier = Modifier.fillMaxSize(),
                    pageText = pageText,
                    pageCount = pageCount
                )
            }
        }
    }
}

@Composable
private fun StudentEnrollmentValidationFooter(
    onCancel: () -> Unit,
    onContinueReview: () -> Unit,
    canContinue: Boolean
) {
    StudentEnrollmentActionFooter(
        onCancel = onCancel,
        primaryLabel = "Continue Review",
        primaryIcon = painterResource(Res.drawable.arrow_right),
        primaryEnabled = canContinue,
        onPrimaryClick = onContinueReview
    )
}

@Composable
private fun StudentEnrollmentReviewFooter(
    backLabel: String,
    onBackToValidationSummary: () -> Unit,
    onCancel: () -> Unit,
    onSaveRecords: () -> Unit,
    readyToSaveRows: Int,
    canSave: Boolean
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        StudentEnrollmentBackButton(
            label = backLabel,
            onClick = onBackToValidationSummary
        )
        Row(
            horizontalArrangement = Arrangement.spacedBy(16.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            StudentEnrollmentOutlineButton(
                label = "Cancel",
                onClick = onCancel,
                borderColor = StudentEnrollmentBorder,
                contentColor = StudentEnrollmentNavy,
                modifier = Modifier.width(160.dp)
            )
            StudentEnrollmentFilledButton(
                label = "Save Records (${formatCount(readyToSaveRows)})",
                icon = painterResource(Res.drawable.arrow_right),
                onClick = onSaveRecords,
                modifier = Modifier.width(280.dp),
                enabled = canSave
            )
        }
    }
}

@Composable
private fun StudentEnrollmentExistingDetailsCard(
    rows: List<StudentEnrollmentSummaryRow>,
    onDownloadCurrentData: () -> Unit,
    onDownloadTemplate: () -> Unit,
    modifier: Modifier = Modifier
) {
    ImportSummaryCard(
        index = 1,
        title = "Existing Details",
        subtitle = "View summary of current data in the system.",
        icon = painterResource(Res.drawable.database_download),
        iconTint = StudentEnrollmentSuccess,
        iconBackground = StudentEnrollmentSuccessBg,
        rows = rows.map { it.toImportSummaryRow() },
        modifier = modifier,
        cardHeight = 372.dp
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            ImportCardActionButton(
                label = "Download Current Data",
                icon = painterResource(Res.drawable.download),
                onClick = onDownloadCurrentData,
                modifier = Modifier.fillMaxWidth(),
                contentColor = StudentEnrollmentSuccess,
                borderColor = StudentEnrollmentSuccess
            )
            ImportCardActionButton(
                label = "Download Template",
                icon = painterResource(Res.drawable.document),
                onClick = onDownloadTemplate,
                modifier = Modifier.fillMaxWidth(),
                contentColor = StudentEnrollmentPrimary,
                borderColor = StudentEnrollmentPrimary
            )
        }
    }
}

@Composable
private fun StudentEnrollmentNewImportDetailsCard(
    rows: List<StudentEnrollmentSummaryRow>,
    onUpload: () -> Unit,
    modifier: Modifier = Modifier
) {
    val hasSelectedFile = rows.firstOrNull { it.label == "File Name" }
        ?.value
        ?.let { it.isNotBlank() && !it.equals("No file selected", ignoreCase = true) && !it.equals("Not selected", ignoreCase = true) }
        ?: false
    ImportSummaryCard(
        index = 2,
        title = "New Import Details",
        subtitle = "Select a workbook to validate and import.",
        icon = painterResource(Res.drawable.excel_file),
        iconTint = StudentEnrollmentSuccess,
        iconBackground = StudentEnrollmentSuccessBg,
        rows = rows.map { it.toImportSummaryRow() },
        modifier = modifier,
        cardHeight = 372.dp
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            ImportCardActionButton(
                label = if (hasSelectedFile) "Change File" else "Select File",
                icon = painterResource(Res.drawable.cloud_upload),
                onClick = onUpload,
                modifier = Modifier.fillMaxWidth(),
                contentColor = StudentEnrollmentPrimary,
                borderColor = StudentEnrollmentPrimary
            )
            androidx.compose.material3.Text(
                text = "Supported format: .xlsx    |    Max file size: 10MB",
                color = StudentEnrollmentNavy,
                style = androidx.compose.material3.MaterialTheme.typography.bodySmall,
                fontWeight = androidx.compose.ui.text.font.FontWeight.Medium,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

@Composable
private fun StudentEnrollmentValidationSummaryCard(
    rows: List<StudentEnrollmentSummaryRow>,
    lastUpdated: String,
    onValidate: () -> Unit,
    modifier: Modifier = Modifier
) {
    ImportSummaryCard(
        index = 3,
        title = "Validation Summary",
        subtitle = "Validate the uploaded file to check for errors.",
        icon = painterResource(Res.drawable.clipboard_check),
        iconTint = StudentEnrollmentPurple,
        iconBackground = StudentEnrollmentPurpleBg,
        rows = rows.map { it.toImportSummaryRow() },
        modifier = modifier,
        cardHeight = 372.dp
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            ImportCardActionButton(
                label = "Validate File",
                icon = painterResource(Res.drawable.circle_check),
                onClick = onValidate,
                modifier = Modifier.fillMaxWidth(),
                contentColor = StudentEnrollmentPurple,
                borderColor = StudentEnrollmentPurple
            )
            androidx.compose.material3.Text(
                text = lastUpdated,
                color = StudentEnrollmentNavy,
                style = androidx.compose.material3.MaterialTheme.typography.bodySmall,
                fontWeight = androidx.compose.ui.text.font.FontWeight.Medium,
                modifier = Modifier.fillMaxWidth()
            )
        }
    }
}

private fun StudentEnrollmentSummaryRow.toImportSummaryRow(): ImportSummaryRow {
    return ImportSummaryRow(
        label = label,
        value = value,
        valueColor = valueColor,
        badgeText = badgeText,
        badgeBackground = badgeBackground,
        badgeColor = badgeColor
    )
}

private fun StudentEnrollmentReviewSummary.toImportReviewSummary(): ImportReviewSummary {
    return ImportReviewSummary(
        fileName = fileName,
        uploadedOn = uploadedOn,
        readyToSaveRows = readyToSaveRows,
        status = status
    )
}

private fun StudentEnrollmentStepUiModel.toImportStep(): ImportStepUiModel {
    return ImportStepUiModel(
        number = number,
        title = title,
        subtitle = subtitle,
        isActive = isActive,
        isCompleted = isCompleted
    )
}

private fun StudentEnrollmentErrorRow.toImportErrorRow(): ImportErrorRow {
    return ImportErrorRow(
        sheetName = sheetName,
        rowNo = rowNo,
        message = message,
        id = id
    )
}

private fun studentEnrollmentPageCount(rowsPerPage: Int, totalRecords: Int): Int {
    val safeRowsPerPage = rowsPerPage.coerceAtLeast(1)
    val safeTotalRecords = totalRecords.coerceAtLeast(1)
    return ((safeTotalRecords + safeRowsPerPage - 1) / safeRowsPerPage).coerceAtLeast(1)
}

private fun studentEnrollmentPageText(rowsPerPage: Int, pageIndex: Int, totalRecords: Int): String {
    val safeRowsPerPage = rowsPerPage.coerceAtLeast(1)
    val safeTotalRecords = totalRecords.coerceAtLeast(0)
    if (safeTotalRecords == 0) {
        return "Showing 0 to 0 of 0 records"
    }
    val safePageCount = studentEnrollmentPageCount(safeRowsPerPage, safeTotalRecords)
    val safePageIndex = pageIndex.coerceIn(0, safePageCount - 1)
    val start = (safePageIndex * safeRowsPerPage) + 1
    val end = (start + safeRowsPerPage - 1).coerceAtMost(safeTotalRecords)
    return "Showing ${formatCount(start)} to ${formatCount(end)} of ${formatCount(safeTotalRecords)} records"
}

private fun formatCount(value: Int): String {
    return value.toString().reversed().chunked(3).joinToString(",").reversed()
}
